import { C } from '../../theme.js';

// ── AuditTable ──────────────────────────────────────────────────────────
// Shared interactive audit grid for every feature-selection step (Variance
// Threshold, Pearson/Spearman/Kendall Correlation, Chi-Square, ANOVA).
// Columns, fixed: Column Name | Data Type | Calculated Metric | Auto
// Recommendation | User Override. Recomputed fresh from `rows` on every
// render (no memoization here) — a threshold/target/column-selection change
// upstream re-renders this with the new numbers immediately, never a stale
// or static "0 of 0".
//
// `rows`: [{ column, dataType, metric, autoStatus: 'keep'|'drop',
//            override: 'auto'|'keep'|'drop', onOverrideChange(next) }]
export function AuditTable({ headerLabel, countLabel, rows, emptyMessage }) {
  const total = rows.length;
  const droppedCount = rows.filter(r => (r.override && r.override !== 'auto' ? r.override : r.autoStatus) === 'drop').length;

  return (
    <div style={{
      marginTop: 14, background: C.surface, border: `1px solid ${C.border}`,
      borderRadius: 8, overflow: 'hidden'
    }}>
      <div style={{
        padding: '8px 12px', background: 'var(--info-bg)', borderBottom: `1px solid ${C.accentMuted}`,
        display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap'
      }}>
        <span style={{ fontSize: '.76rem', fontWeight: 700, color: C.accentBright }}>
          {headerLabel}
        </span>
        {total > 0 && (
          <span style={{ fontSize: '.72rem', color: C.fg4 }}>
            {countLabel || `${droppedCount} of ${total} slated for removal`}
          </span>
        )}
      </div>

      <div style={{ maxHeight: 280, overflowY: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '.76rem', textAlign: 'left' }}>
          <thead>
            <tr style={{ borderBottom: `1px solid ${C.borderSubtle}`, color: C.fg4 }}>
              <th style={{ padding: '6px 10px', fontWeight: 600 }}>Column Name</th>
              <th style={{ padding: '6px 10px', fontWeight: 600 }}>Data Type</th>
              <th style={{ padding: '6px 10px', fontWeight: 600 }}>Calculated Metric</th>
              <th style={{ padding: '6px 10px', fontWeight: 600 }}>Auto Recommendation</th>
              <th style={{ padding: '6px 10px', fontWeight: 600, textAlign: 'right' }}>User Override</th>
            </tr>
          </thead>
          <tbody>
            {total === 0 ? (
              <tr>
                <td colSpan={5} style={{ padding: 12, textAlign: 'center', color: C.fg5 }}>
                  {emptyMessage || 'No candidate features found.'}
                </td>
              </tr>
            ) : (
              rows.map(row => {
                const overridden = row.override && row.override !== 'auto';
                const finalStatus = overridden ? row.override : row.autoStatus;
                const isDrop = finalStatus === 'drop';
                return (
                  <tr key={row.column} style={{
                    borderBottom: `1px solid ${C.borderSubtle}`,
                    background: isDrop ? 'rgba(239, 68, 68, 0.04)' : 'transparent'
                  }}>
                    <td style={{ padding: '6px 10px', color: C.fg1, fontWeight: 500 }}>{row.column}</td>
                    <td style={{ padding: '6px 10px', color: C.fg4, fontFamily: "'JetBrains Mono', monospace" }}>
                      {row.dataType || 'unknown'}
                    </td>
                    <td style={{ padding: '6px 10px', color: C.fg3, fontFamily: "'JetBrains Mono', monospace" }}>
                      {row.metric}
                    </td>
                    <td style={{ padding: '6px 10px' }}>
                      <span style={{
                        fontSize: '.68rem', fontWeight: 700,
                        padding: '2px 8px', borderRadius: 4,
                        background: row.autoStatus === 'drop' ? 'rgba(239, 68, 68, 0.15)' : 'rgba(16, 185, 129, 0.15)',
                        color: row.autoStatus === 'drop' ? '#ef4444' : '#10b981'
                      }}>
                        {row.autoStatus === 'drop' ? 'Drop' : 'Keep'}
                      </span>
                      {overridden && (
                        <span style={{
                          marginLeft: 6, fontSize: '.62rem', padding: '1px 5px', borderRadius: 4,
                          background: 'rgba(245, 158, 11, 0.15)', color: '#f59e0b',
                          border: '1px solid rgba(245, 158, 11, 0.3)', fontWeight: 600
                        }}>
                          Overridden
                        </span>
                      )}
                    </td>
                    <td style={{ padding: '6px 10px', textAlign: 'right' }}>
                      <select
                        value={row.override || 'auto'}
                        onChange={(e) => row.onOverrideChange(e.target.value)}
                        style={{
                          background: C.input, color: C.fg1,
                          border: `1px solid ${C.borderStrong}`, borderRadius: 6,
                          padding: '3px 6px', fontSize: '.72rem', outline: 'none',
                          fontFamily: 'inherit', cursor: 'pointer'
                        }}
                      >
                        <option value="auto">Auto ({row.autoStatus === 'drop' ? 'Drop' : 'Keep'})</option>
                        <option value="keep">Force Keep</option>
                        <option value="drop">Force Drop</option>
                      </select>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
